import { apiClient } from './client';
import { useQuery, useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';

export interface SettlementItem {
  name: string;
  energy?: number | null;
  price?: number | null;
  fee?: number | null;
}

export interface MechanismProject {
  project_code: string;
  project_name: string;
  settled_energy?: number | null;
  mechanism_price?: number | null;
  reference_price?: number | null;
  energy_ratio?: number | null;
  diff_fee?: number | null;
  annual_energy?: number | null;
}

export interface MonthlySettlementRow {
  station_name: string;
  account_name: string;
  settlement_month: string;
  settlement_start: string;
  settlement_end: string;
  total_energy: number;
  total_avg_price: number;
  total_fee: number;
  source_file: string;
  summary_items: SettlementItem[];
  mechanism_projects: MechanismProject[];
}

export interface MonthlySettlementData {
  rows: MonthlySettlementRow[];
  available_months: string[];
  available_stations: string[];
}

export interface MonthlySettlementResponse {
  code: string;
  message: string;
  data: MonthlySettlementData;
}

export interface SettlementUploadResult {
  success: boolean;
  station: string;
  date: string;
  row_count: number;
  validation_ok: boolean;
  validation_diff: number;
  phase_count: number;
  phase_total: number;
  total_fee: number;
}

export interface UploadResponse {
  code: string;
  message: string;
  data: SettlementUploadResult;
}

export function useMonthlySettlement(params: {
  station_name?: string;
  months?: string[];
}) {
  return useQuery<MonthlySettlementResponse, AxiosError>({
    queryKey: ['monthly-settlement', 'data', params],
    queryFn: () =>
      apiClient.get('/monthly-settlement/data', {
        params,
        paramsSerializer: { indexes: null },
      }).then((r: AxiosResponse<MonthlySettlementResponse>) => r.data),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

export function useMonthlySettlementUpload() {
  return useMutation<UploadResponse, AxiosError, { file: File }>({
    mutationFn: ({ file }) => {
      const fd = new FormData();
      fd.append('file', file);
      return apiClient.post('/monthly-settlement/upload', fd).then(
        (r: AxiosResponse<UploadResponse>) => r.data
      );
    },
  });
}
